import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { Conversation, Message } from "../domain/models/chatbot";
import { IChatbotDisplay } from "../domain/ports/i-chatbot-display";
import { ChatbotManage } from "./chatbot-manage";

@Injectable()
export class ChatbotDisplayer implements IChatbotDisplay {
  private messagesSubject = new BehaviorSubject<Message[]>([]);
  readonly messages$ = this.messagesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$ = this.errorSubject.asObservable();

  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  readonly conversations$ = this.conversationsSubject.asObservable();

  private scrollTriggerSubject = new Subject<void>();
  readonly scrollTrigger$ = this.scrollTriggerSubject.asObservable();

  private activeConversationIdSubject = new BehaviorSubject<string | null>(
    null
  );
  readonly activeConversationId$ =
    this.activeConversationIdSubject.asObservable();

  private readonly UNKNOW_ERROR =
    "An unknown error occurred. Please try again later.";

  private responseStopped = false;

  private lastInteractionIndex = 0;

  constructor(private manage: ChatbotManage) { }

  async sendMessage(message: string, userId: number, theme: string, questionType: 'Retry' | 'Edit' | 'Original' , shouldStop: () => boolean): Promise<void> {
    if (!message.trim()) return;
    this.responseStopped = false;

    const interactionIndex = this.lastInteractionIndex++;


    this.addMessage({
      id: Date.now(),
      role: "user",
      content: message,
      interaction_index: interactionIndex
    });

    this.loadingSubject.next(true);

    try {
      const responsePromise = this.manage.askGpt(
        message,
        this.activeConversationIdSubject.value,
        userId,
        theme,
        questionType
      );

      let response: any = null;
      let done = false;
      responsePromise.then((res) => {
        response = res;
        done = true;
      });

      while (!done) {
        if (shouldStop()) {
          this.loadingSubject.next(false);
          return;
        }

        await new Promise((r) => setTimeout(r, 100));
      }

      if (this.responseStopped) {
        return;
      }

      this.activeConversationIdSubject.next(
        response.data.conversation_id || null
      );


      this.addMessage({
        id: Date.now(),
        role: "bot",
        content: response.data.answer,
        interaction_index: interactionIndex
      });

      if (this.activeConversationIdSubject.value) {
        this.addConversation({
          id: this.activeConversationIdSubject.value,
          title: message,
        });
      }
    } catch (error: any) {
      this.errorSubject.next(error.message || this.UNKNOW_ERROR);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  forceStopResponse() {
    this.loadingSubject.next(false);
    this.responseStopped = true;

    const messages = this.messagesSubject.value;

    const lastUserIdx = [...messages].reverse().findIndex(m => m.role === 'user');
    const lastUserAbsIdx = lastUserIdx !== -1 ? messages.length - 1 - lastUserIdx : -1;

    if (
      lastUserAbsIdx !== -1 &&
      !messages.slice(lastUserAbsIdx + 1).some(m => m.role === 'bot')
    ) {
      this.addMessage({
        id: Date.now(),
        role: "bot",
        content: "Response interrupted.",
      });
    }
  }

  addMessage(msg: Message) {
    const current = this.messagesSubject.value;
    this.messagesSubject.next([...current, msg]);
    this.scrollTriggerSubject.next();
  }

  async downloadDocument(documentName: string): Promise<void> {
    if (!documentName.trim()) return;
    try {
      const response = await this.manage.downloadDocument(documentName);

      const byteCharacters = atob(response.data.raw);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.contentType });

      this.downloadBlob(blob, documentName);
    } catch (error: any) {
      this.errorSubject.next(error.message || this.UNKNOW_ERROR);
    }
  }

  copyMessage(message: Message) {
    const contentExtracted = this.extractTextFromMessageHtml(message.content);

    try {
      navigator.clipboard.writeText(contentExtracted.trim());

      const messages = this.messagesSubject.value;
      const updatedMessages = messages.map((m) =>
        m.id === message.id && m.role === "bot"
          ? { ...m, copied: true }
          : { ...m, copied: false }
      );
      this.messagesSubject.next(updatedMessages);

      const currentConversationId = this.activeConversationIdSubject.value;

      setTimeout(() => {
        if (this.activeConversationIdSubject.value !== currentConversationId)
          return;

        const resetMessages = this.messagesSubject.value.map((m) =>
          m.copied ? { ...m, copied: false } : m
        );
        this.messagesSubject.next(resetMessages);
      }, 2000);
    } catch (error: any) {
      this.errorSubject.next(error.message || this.UNKNOW_ERROR);
    }
  }

  private extractTextFromMessageHtml(content: string): string {
    if (!content) return "";

    // 1. <div class='answer-images'>...</div> → [Visual reference]
    content = content.replace(
      /<div class=['"]answer-images['"][^>]*>[\s\S]*?<\/div>/gi,
      "[Visual reference]\n"
    );

    // 2. <span ...><del>...</del></span> (case: file does not exist) → [File reference does not exist]
    content = content.replace(
      /<span[^>]*>\s*<del>[^<]+<\/del>\s*<\/span>/gi,
      "[File reference does not exist]"
    );

    // 3. <div class='file-attachment-container'><a class='file-attachment' data-file='{fileName}'>{linkText}</a></div>
    //    → linkText
    content = content.replace(
      /<div class=['"]file-attachment-container['"][^>]*>\s*<a class=['"]file-attachment['"][^>]*>([\s\S]*?)<\/a>\s*<\/div>/gi,
      (_match, linkText) => linkText
    );

    // 4. <h5>{text}</h5> → text
    content = content.replace(/<h5[^>]*>([\s\S]*?)<\/h5>/gi, "$1");

    // 5. <div>...</div> → ...
    content = content.replace(/<div[^>]*>([\s\S]*?)<\/div>/gi, "$1");

    // 6. <br> o <br/> → line break
    content = content.replace(/<br\s*\/?>/gi, "\n");

    // 7. Delete tags <b>, <i>, <u>, <span>, <a>, <strong>
    content = content.replace(/<\/?(b|i|u|span|a|strong)[^>]*>/gi, "");

    // 8. Others HTML tags
    content = content.replace(/<\/?[^>]+>/g, "");

    // 9. Normalize whitespace
    content = content.replace(/\n{3,}/g, "\n\n").trim();

    return content;
  }

  listenMessage(message: Message) {
    const contentExtracted = this.extractTextFromMessageHtml(message.content);
    const utterance = new SpeechSynthesisUtterance(contentExtracted);
    speechSynthesis.speak(utterance);
  }

  createNewChat() {
    this.activeConversationIdSubject.next(null);
    this.messagesSubject.next([]);
    this.lastInteractionIndex = 0;
  }

  async getConversations(userId: number, pageNumber: number = 1, pageSize: number = 10, useCache: boolean = false, search: string = ""): Promise<void> {
    const response = await this.manage.getHistory(userId, pageNumber, pageSize, useCache, search);

    let conversations: Conversation[] = [];

    if (Array.isArray(response.data)) {
      conversations = response.data.map(
        (conversation: any): Conversation => ({
          id: conversation.conversation_id,
          title: conversation.first_history_element,
          pinned: conversation.pinned || false,
        })
      );
    }

    if (pageNumber > 1) {
      this.conversationsSubject.next([
        ...this.conversationsSubject.value,
        ...conversations
      ]);
    } else {
      this.conversationsSubject.next(conversations);
    }
  }

  addConversation(conversation: Conversation) {
    const exists = this.conversationsSubject.value.some(
      (c) => c.id === conversation.id
    );
    if (!exists) {
      const conversations = this.conversationsSubject.value;
      const pinned = conversations.filter(c => c.pinned);
      const unpinned = conversations.filter(c => !c.pinned);
      this.conversationsSubject.next([
        ...pinned,
        conversation,
        ...unpinned
      ]);
    }
  }

  async selectConversation(id: string): Promise<void> {
    const response = await this.manage.getConversation(id);

    if (Array.isArray(response.data)) {
      let idx = 0;
      const messages = response.data.flatMap((item: any) => [
        {
          id: Date.now() + idx * 2,
          role: "user",
          content: item.question,
          interaction_index: idx
        },
        {
          id: Date.now() + idx * 2 + 1,
          role: "bot",
          content: item.answer,
          interaction_index: idx
        },
        idx++
      ]);
      this.messagesSubject.next(messages);
      this.activeConversationIdSubject.next(id);

      this.lastInteractionIndex = messages.length > 0
        ? Math.max(...messages.map(m => m.interaction_index ?? 0))
        : 0;
    } else {
      this.messagesSubject.next([]);
    }
  }

  async likeMessage(userId: number, message: Message) {
    const updatedMessages = this.messagesSubject.value.map((m) =>
      m.id === message.id
        ? { ...m, liked: !m.liked, disliked: false }
        : m
    );
    this.messagesSubject.next(updatedMessages);

    try {
      await this.manage.rateAnswer(
        userId,
        this.activeConversationIdSubject.value ?? "",
        String(message.interaction_index),
        "like"
      );
    } catch (error) {
      this.errorSubject.next("Error rating answer");
    }
  }

  async dislikeMessage(userId: number, message: Message, comment?: string) {
    const updatedMessages = this.messagesSubject.value.map((m) =>
      m.id === message.id
        ? { ...m, disliked: !m.disliked, liked: false }
        : m
    );
    this.messagesSubject.next(updatedMessages);

    try {
      await this.manage.rateAnswer(
        userId,
        this.activeConversationIdSubject.value ?? "",
        String(message.interaction_index),
        "dislike",
        comment
      );
    } catch (error) {
      this.errorSubject.next("Error rating answer");
    }
  }

  removeMessages(index: number[]) {
    index.sort((a, b) => b - a);
    const msgs = [...this.messagesSubject.value];
    for (const idx of index) {
      msgs.splice(idx, 1);
    }
    this.messagesSubject.next(msgs);
  }

  async updateConversation(conversationId: string, userId: number, rename: string, pinned: boolean, deleted: boolean, action: "renaming" | "deleting" | "pinning"): Promise<void> {
    try {
      await this.manage.updateConversation(conversationId, userId, rename, pinned, deleted);

      let conversations = this.conversationsSubject.value;

      if (action === "deleting") {
        conversations = conversations.filter(c => c.id !== conversationId);
      } else if (action === "renaming") {
        conversations = conversations.map(c =>
          c.id === conversationId ? { ...c, title: rename } : c
        );
      } else if (action === "pinning") {
        conversations = conversations.map(c =>
          c.id === conversationId ? { ...c, pinned } : c
        );
        conversations = [
          ...conversations.filter(c => c.pinned),
          ...conversations.filter(c => !c.pinned)
        ];
      }

      this.conversationsSubject.next(conversations);
    } catch (error) {
      this.errorSubject.next("Error updating conversation");
    }
  }

  async submitComment(userId: number, comment: string): Promise<any> {
    try {
      await this.manage.createComment(userId, comment);
    } catch (error) {
      this.errorSubject.next("Error submitting comment");
    }
  }
  
  async exportConversationToPdf(id: string): Promise<void> {
    try {
      const blob = await this.manage.exportConversationToPdf(id);
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const fileName = `MS_AI_Agent_Chat_${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear().toString().slice(-2)}.pdf`;
      this.downloadBlob(blob, fileName);
    } catch (error: any) {
      this.errorSubject.next(error.message || this.UNKNOW_ERROR);
      throw error;
    }
  }

  async shareConversation(id: string, emails: string[], mailContent: string, subject: string): Promise<void> {
    try {
      await this.manage.shareConversation(id, emails, mailContent, subject);
    } catch (error: any) {
      this.errorSubject.next(error.message || this.UNKNOW_ERROR);
      throw error;
    }
  }

  private downloadBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
